import React from "react";
import {
    Container,
    Box,
    Typography,
    List,
    ListItem,
    Divider,
    Link,
} from "@mui/material";

export default function PrivacyPolicy() {
    return (
        <Container maxWidth="md">
            <Box sx={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
                {/* Title */}
                <Typography
                    variant="h4"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ textAlign: "center" }}
                >
                    PRIVACY NOTICE
                </Typography>
                <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ textAlign: "center" }}
                >
                    (MTRCB Mobile App)
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* ABOUT US */}
                <Typography id="about-us" variant="h5" gutterBottom>
                    ABOUT US
                </Typography>
                <Typography paragraph>
                    Movie and Television Review and Classification Board (“MTRCB”, “we”, “us” or “our”) is a government agency mandated to regulate and supervise the production, exhibition, and distribution of motion pictures, television programs, and other similar visual media. We ensure that content shown to the Filipino public is appropriately classified, promoting responsible viewing habits and protecting the welfare of audiences, especially children.
                </Typography>

                {/* PRIVACY STATEMENT */}
                <Typography id="privacy-statement" variant="h5" gutterBottom mt={4}>
                    PRIVACY STATEMENT
                </Typography>
                <Typography paragraph>
                    At MTRCB, we recognize and deeply value the fundamental human right to privacy, as enshrined in the Philippine Constitution and reinforced by the Data Privacy Act of 2012 (Republic Act No. 10173). In fulfilling our public service mandate, we are committed to upholding the highest standards of data protection and privacy in all our operations. We believe that a strong commitment to privacy is integral to maintaining public trust and effectively serving the Filipino people.
                </Typography>

                {/* THIS PRIVACY NOTICE */}
                <Typography id="this-notice" variant="h5" gutterBottom mt={4}>
                    THIS PRIVACY NOTICE
                </Typography>
                <Typography paragraph>
                    This Privacy Notice describes how we collect, use, store, disclose, dispose and protect your Personal Data as we pursue our advocacy for our stakeholders and provide our Services through the MTRCB Mobile App.
                </Typography>
                <Typography paragraph>
                    The words “Personal Data”, “Personal Information”, “Sensitive Personal Information”, “Processing of Personal Data”, and other related terminologies in this Privacy Notice are used in the same context as they are found in the DPA, its Implementing Rules and Regulations (IRR) and other relevant National Privacy Commission (NPC) issuances and other applicable data privacy laws (collectively “Data Privacy Laws”).
                </Typography>

                <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Personal Data</b> refers to both personal and sensitive personal information.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Personal Information</b> is any information or set of information that can be used to identify an individual or can reasonably and directly identify an individual.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Sensitive Personal Information</b> is any information about individual’s race, ethnic origin, marital status, age, color, and religious, philosophical or political affiliations; individual’s health or education; about any criminal, civil or administrative proceeding of an individual; unique government issued identifiers; and those established by law as classified, as defined under applicable Data Privacy Laws.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Processing of Personal Data</b> refers to any operation or any set of operations performed upon personal data including, but not limited to, the collection, use, storage, disclosure or disposal.
                    </ListItem>
                </List>

                <Typography paragraph>
                    This Privacy Notice does not cover the practices of companies we do not own or control or people we do not manage and this Privacy Notice does not apply to any third-party sites that may link to or be accessible from our Mobile App.
                </Typography>

                {/* OUR BASIS FOR PROCESSING */}
                <Typography id="basis" variant="h5" gutterBottom mt={4}>
                    OUR BASIS FOR PROCESSING YOUR PERSONAL DATA
                </Typography>
                <Typography paragraph>
                    As we conduct our business and offer our services, we may process your Personal Data in accordance with the following lawful basis:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Consent.</b> Your consent, which you may withdraw anytime unless there is other basis for processing.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Lawful Rights.</b> To protect our lawful rights and interests in court proceedings or to establish, exercise or defend legal claims.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Legitimate Interest.</b> For our legitimate business interests.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Legal Obligation.</b> To comply with our legal obligations or as may be required by law.
                    </ListItem>
                </List>

                {/* PERSONAL DATA WE COLLECT */}
                <Typography id="data-we-collect" variant="h5" gutterBottom mt={4}>
                    PERSONAL DATA WE COLLECT
                </Typography>
                <Typography paragraph>
                    As you download the mobile app, create your account, log in your account, use the app or submit reports, we may collect the following Personal Data:
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" mt={2}>
                    A. Public
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>Name</ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Contact details: email address and phone number
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>Government issued ID</ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Mobile app permission: microphone, photo and video gallery (disallowed by default)
                    </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" mt={2}>
                    B. Deputy / MTRCB Employee
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>Name and Control Number</ListItem>
                    <ListItem sx={{ display: "list-item" }}>Deputy year (for deputies)</ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Contact details: email address and phone number
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Mobile app permission: microphone, photo and video gallery, camera (disallowed by default)
                    </ListItem>
                </List>

                <Typography paragraph>
                    There may also be instances when we may request Personal Data that is not enumerated above, in such a case, we will properly notify you. You are under no obligation to provide any such information. However, if you choose to withhold requested information, we may not be able to assist you or provide you with our services or you may not be able to use the Mobile App.
                </Typography>
                <Typography paragraph>
                    We may also collect information that does not identify you individually. This may include anonymous, aggregated, analytical or statistical data; usage statistics and other forms of anonymized data that we may get from your use of the Mobile App or otherwise liaising with us.
                </Typography>

                {/* SOURCES OF YOUR PERSONAL DATA */}
                <Typography id="sources" variant="h5" gutterBottom mt={4}>
                    SOURCES OF YOUR PERSONAL DATA
                </Typography>
                <Typography paragraph>
                    Most of the Personal Data we process are provided to us directly by you when:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>You download the Mobile App.</ListItem>
                    <ListItem sx={{ display: "list-item" }}>Create your account during sign up.</ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Login to your account to browse or use the Mobile App, transact or contact us through or submit a report violation seen involving movies, trailers, TV programs, movie posters or public ads.
                    </ListItem>
                </List>

                {/* PURPOSE FOR PROCESSING */}
                <Typography id="purpose" variant="h5" gutterBottom mt={4}>
                    OUR PURPOSE FOR PROCESSING
                </Typography>
                <Typography paragraph>
                    We only collect the Personal Data for the following purposes:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        To facilitate public and deputy reporting of violations.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        To provide information about MTRCB to the public.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        To share information about movies, TV schedules announcements and classifications, share news and updates, share information about MTRCB and provide information we think you may find useful.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        To conduct an initial assessment of violations reported and refer the same to Monitoring and Inspection Unit for proper action.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        For compliance with our legal obligations, regulatory requirements and for other legal purposes:
                        <List sx={{ listStyleType: "lower-alpha", pl: 4, mt: 1 }}>
                            <ListItem sx={{ display: "list-item" }}>
                                To process any reports or complaints, implement preventive measures and to investigate act, omission, or misconduct that would constitute a violation of applicable MTRCB issuances.
                            </ListItem>
                            <ListItem sx={{ display: "list-item" }}>
                                To gather the necessary information required by law, record keeping and good regulatory practices.
                            </ListItem>
                            <ListItem sx={{ display: "list-item" }}>
                                To perform our mandate and comply with regulatory requirements.
                            </ListItem>
                            <ListItem sx={{ display: "list-item" }}>
                                To protect MTRCB’s lawful rights and interests in court proceedings and to establish, exercise or as defense from legal claims.
                            </ListItem>
                        </List>
                    </ListItem>
                </List>
                <Typography paragraph>
                    As of the latest date of the Privacy Notice we DO NOT engage in automated profiling or automated decision making based on your personal data that produce legal or similarly significant effects concerning you.
                </Typography>

                {/* DATA DISCLOSURE AND SHARING */}
                <Typography id="disclosure" variant="h5" gutterBottom mt={4}>
                    DATA DISCLOSURE AND SHARING
                </Typography>
                <Typography paragraph>
                    With the use of the Mobile App, we may disclose and share your Personal Data to the following:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        To third-party service providers engaged by us who may process Personal Data on our behalf as a result of the service they perform. These service providers will only process your Personal Data upon our instructions.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        To law enforcement agencies, government authorities, courts or tribunal when we further investigate on the report you have submitted.
                    </ListItem>
                </List>
                <Typography paragraph>
                    Rest assured that, in sharing your personal data, MTRCB will ensure that your rights as a data subject are protected and respected. We will only share your Personal Data upon your consent, when necessary, or when allowed by the DPA and other relevant rules and regulations. We ensure the Personal Data being shared to the above listed recipients is not excessive in relation to the declared and specified purpose.
                </Typography>

                {/* DATA RETENTION */}
                <Typography id="retention" variant="h5" gutterBottom mt={4}>
                    DATA RETENTION
                </Typography>
                <Typography paragraph>
                    Electronic records are securely stored in local drives within our office and in the Mobile App’s cloud storage facility which is located outside the Philippines.
                </Typography>
                <Typography paragraph>
                    We will retain your Personal Data for as long as necessary to fulfill the purposes for which it was collected or as may be required by applicable law, rules and regulations.
                </Typography>
                <Typography paragraph>
                    Accounts will be deleted after 2 years of inactivity (for the public) or 2 years from termination of deputization or employment (for deputies or employees).
                </Typography>
                <Typography paragraph>
                    Once it is ascertained that retention is no longer necessary, we will securely dispose your Personal Data in accordance with our internal procedure such as deletion or purging of electronic data including its backups, and through such other means that would prevent further processing.
                </Typography>

                {/* DATA PROTECTION MEASURES */}
                <Typography id="protection" variant="h5" gutterBottom mt={4}>
                    OUR DATA PROTECTION MEASURES
                </Typography>
                <Typography paragraph>
                    We implement reasonable and appropriate technical, physical and organizational security measures to protect your Personal Data from unauthorized disclosure, misuse, alteration, loss or destruction.
                </Typography>
                <Typography paragraph>
                    While we cannot guarantee the absolute security and impenetrability of our database, we take all the necessary measures, according to reasonable industry standards and practices to keep your data safe through the establishment of security controls, such as, but not limited to:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        We implement data privacy and protection policies to provide standards and guidelines on how we handle personal data we process.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        We ensure that our data processing systems are secured against unauthorized access through regularly review, monitoring and testing to ensure that the security and integrity of the system is not compromised.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        We regularly monitor security incidents and implement process for taking appropriate preventive, corrective and mitigating action.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        Ensuring physical security measure where processing facilities are only accessible by authorized personnel only.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        We ensure that our employees, agents, vendors and anyone who is involved in the processing of your personal data shall operate under strict confidentiality.
                    </ListItem>
                </List>

                {/* YOUR ROLE */}
                <Typography id="your-role" variant="h5" gutterBottom mt={4}>
                    YOUR ROLE IN DATA PROTECTION
                </Typography>
                <Typography paragraph>
                    While we try to keep our systems safe, we cannot control external factors (such as the safety of your computer or mobile phone, the network you are using, the servers you are being routed through, and the like). As our partner in safeguarding your Personal Data, we rely on your commitment:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        To employ the necessary measures to likewise protect your Personal Data, including keeping your passwords and security information safe and updated.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        To communicate with us only through our official support channels, while taking appropriate security and confidentiality safeguards necessary on your end.
                    </ListItem>
                </List>

                {/* DATA SUBJECT RIGHTS */}
                <Typography id="rights" variant="h5" gutterBottom mt={4}>
                    YOUR DATA SUBJECT RIGHTS
                </Typography>
                <Typography paragraph>
                    Under the DPA, you have the following rights:
                </Typography>
                <List sx={{ listStyleType: "decimal", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to be informed.</b> You have the right to be informed prior to data collection of how your data will be collected, used, stored, shared and disposed. We uphold this right through this Privacy Notice.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to object.</b> You have the right to say no to our processing of your personal data or withdraw your consent previously given.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right of access.</b> You have the right to ask us information on how we process your Personal Data.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to rectification.</b> You have the right to ask us to modify or correct your personal data when the same is incomplete, inaccurate, outdated or false.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to erasure or blocking.</b> You have the right to ask us to delete or block the use of your Personal Data, when there is no other existing basis for processing and retaining the same.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to data portability.</b> You have the right to ask that we provide a copy of your data to another organization, or to you, in certain circumstances.
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                        <b>Right to file a complaint and ask for damages.</b> You have the right to file a complaint if you think that your data subject rights have been violated, or if you sustained damages due to any inaccurate, incomplete, outdated, false, unlawfully obtained or unauthorized use of your personal information, you may escalate your concern to our Data Protection Officer so we can assist you:
                    </ListItem>
                </List>

                <Box sx={{ pl: 4, mt: -1 }}>
                    <Typography>Roberto C. Diciembre</Typography>
                    <Typography>
                        <Link href="mailto:dpo@mtrcb.gov.ph">dpo@mtrcb.gov.ph</Link>
                    </Typography>
                    <Typography>
                        MTRCB Bldg. No. 18 Timog Ave.,<br />
                        Quezon City, Metro Manila Philippines
                    </Typography>
                </Box>

                <Typography paragraph mt={2}>
                    In case we failed to resolve your complaint or in you are unsatisfied with the way we handle the matter, you have the right to file a complaint with the National Privacy Commission (NPC).
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" mt={2}>
                    How to exercise your data privacy rights
                </Typography>
                <Typography paragraph>
                    If you want to exercise any of your data subject rights, please fill out our Data Subject Request Form. Submit the accomplished form together with the necessary supporting documents via email at:{" "}
                    <Link href="mailto:dpo@mtrcb.gov.ph">dpo@mtrcb.gov.ph</Link>.
                </Typography>

                {/* CHANGES TO THIS PRIVACY NOTICE */}
                <Typography id="changes" variant="h5" gutterBottom mt={4}>
                    CHANGES TO THIS PRIVACY NOTICE
                </Typography>
                <Typography paragraph>
                    Please be advised that we may revise this Privacy Notice from time to time, as may be necessary to reflect any changes in our processing activities, policies and procedures, as well as to keep up with developments in applicable laws, rules and regulations. Please be assured that any such changes will be posted in the Mobile App.
                </Typography>

                {/* Versioning */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2">Version Date: August 2025</Typography>
                <Typography variant="body2" gutterBottom>Version No.: 1.0</Typography>
            </Box>
        </Container>
    );
}
